<template>
  <div
    :id="`id_${id}`"
    :style="[indent]"
  >
    <v-toolbar
      dense
      class="tree-toolbar mb-1"
      :class="[bgColor, { 'focus-node': isFocused, 'bgColor': nodeItem.active }]"
      :elevation="3"
    >
      <!-- checkbox if select mode  -->
      <VCheckbox
        v-if="isSelectMode"
        v-model="mutableNodeItem.isSelected"
        hideDetails
        class="checkBox"
        data-cy="selectRow"
        @input="checkBoxValueChanged"
      />
      <!-- End checkbox -->
      <!-- Left arrow icon -->
      <v-btn
        v-if="mutableNodeItem.active"
        icon
        data-cy="leftArrow"
        :data-node-ref="nodeItem.ref"
        @click.stop="toggleChildren(null)"
      >
        <v-icon
          v-show="nodeType !== SITE && isEditable"
          large
          class="hidden-md-and-down"
          color="primary"
        >
          mdi-drag-vertical
        </v-icon>
        <v-icon
          large
          color="primary"
        >
          {{ iconArrow }}
        </v-icon>
        <div
          class="d-flex justify-center align-center"
          style="border: 1px solid #26384B; border-radius: 4px; width: 26px; height: 26px"
        >
          <v-icon
            small
            color="#26384B"
          >
            {{ iconName }}
          </v-icon>
        </div>
      </v-btn>
      <!-- text right after icon-->
      <div
        class="bloc-tree-title"
        @click="mutableNodeItem.active ? toggleChildren(null) : null"
      >
        <span
          class="siteTree-title"
        >
          {{ nodeTitle }}
        </span>
      </div>

      <v-spacer />
      <!-- 3 dot icon -->
      <v-toolbar-items v-if="isViewFromSiteTree">
        <v-menu offsetY>
          <template #activator="{ on }">
            <div class="d-flex align-center">
              <v-btn
                color="primary"
                xSmall
                fab
                rounded
                :disabled="!site.active"
                :data-cy="getNodeName"
                v-on="on"
              >
                <v-icon>
                  mdi-dots-vertical
                </v-icon>
              </v-btn>
            </div>
          </template>

          <v-list>
            <!-- Add Analysis -->
            <ListItem
              v-if="isActive && hasPermission('nodes.children.add') && hasPermission('nodes.types.view')"
              :icon="mutableNodeItem.nodeType === COMPONENT ? 'mdi-plus-minus-variant' : 'mdi-plus'"
              :title="addElement"
              @click="onLockedMenuItemClick(addNode, {
                node: mutableNodeItem,
                parentNode,
                siteId,
                compTitle: addAnalysisFormTitle.editAnalysis,
                actionName:addAnalysisFormTitle.editAnalysis
              })"
            />
            <!-- Add FlowMeter -->
            <ListItem
              v-if="isActive && hasPermission('nodes.children.add') && hasPermission('nodes.types.view') && mutableNodeItem.nodeType === COMPONENT"
              icon="mdi-speedometer"
              :title="$t('sites.nodes.addFlowmeter')"
              @click="onLockedMenuItemClick(addNode, {
                node: mutableNodeItem,
                parentNode,
                siteId,
                compTitle: addAnalysisFormTitle.addFlowmeter,
                showBreadcrumbs: true,
                actionName: addAnalysisFormTitle.addFlowmeter,
                'type': [WATER_CONSUMPTION_ANALYSIS_TYPE]
              })"
            />

            <!-- Add Product level -->
            <ListItem
              v-if="isActive && hasPermission('nodes.children.add') && hasPermission('nodes.types.view') && mutableNodeItem.nodeType === COMPONENT"
              icon="mdi-barrel"
              :title="$t('sites.nodes.addProductLevel')"
              @click="onLockedMenuItemClick(addNode, {
                node: mutableNodeItem,
                parentNode,
                siteId,
                compTitle: addAnalysisFormTitle.addProductLevel,
                showBreadcrumbs: true,
                actionName: addAnalysisFormTitle.addProductLevel,
                'type': [PRODUCT_CONSUMPTION_ANALYSIS_TYPE]
              })"
            />
            <!-- Add Fuel consumption -->
            <ListItem
              v-if="isActive && hasPermission('nodes.children.add') && hasPermission('nodes.types.view') && mutableNodeItem.nodeType === COMPONENT"
              icon="mdi-lightning-bolt-circle"
              :title="$t('sites.nodes.addFuelConsumption')"
              @click="onLockedMenuItemClick(addNode, {
                node: mutableNodeItem,
                parentNode,
                siteId,
                showBreadcrumbs: true,
                compTitle: addAnalysisFormTitle.addFuelConsumption,
                actionName: addNode,
                'type': [FUEL_CONSUMPTION_ANALYSIS_TYPE]
              })"
            />

            <!-- Flow Diagrams -->
            <ListItem
              v-if="showItem && hasPermission('flowDiagrams.view')"
              icon="mdi-pipe-disconnected"
              :title="$t('sites.flowDiagram')"
              @click="redirectToComponentOrNetworkRoute('flowDiagram', 'componentFlowDiagram')"
            />

            <!-- Technical Files -->
            <ListItem
              v-if="isActive && !!mutableNodeItem.nodeType && !(mutableNodeItem.nodeType === SITE || mutableNodeItem.nodeType === CONTAINER)"
              icon="mdi-file-document-outline"
              :title="$t('sites.tooltip.technicalFile')"
              @click="showTechnicalFile({...mutableNodeItem, parentNode, showBreadcrumbs: true})"
            />

            <!-- Treatment Programs -->
            <ListItem
              v-if="showItem && hasPermission('treatments.view')"
              icon="mdi-water-outline"
              :title="$t('treatmentPrograms.pageName')"
              @click="redirectToComponentOrNetworkRoute('treatmentProgram', 'componentTreatmentProgram')"
            />
            <!-- Maintenance Schedules -->
            <ListItem
              v-if="showItem && hasPermission('maintenanceSchedules.view')"
              icon="mdi-wrench"
              :title="$t('sitePage.schedule')"
              @click="redirectToComponentOrNetworkRoute('schedule', 'componentSchedule')"
            />
            <!-- Control chart-->
            <ListItem
              v-if="nodeType === COMPONENT && isActive && hasPermission('nodes.edit')"
              icon="mdi-chart-multiple"
              :title="$t('controlChart.title')"
              @click="onLockedMenuItemClick('controlChart', {
                node: mutableNodeItem,
                parentNode,
                showBreadcrumbs: true,
                actionName: 'controlChart'
              })"
            />
            <!-- Legionella Control Chart -->
            <ListItem
              v-if="showItem && hasPermission('flowDiagrams.view')"
              icon="mdi-pipe-disconnected"
              :title="$t('sites.legionellaChart')"
              @click="redirectToComponentOrNetworkRoute('legionellaChart', 'componentLegionellaChart')"
            />
            <!-- Legionella Risk Assessments -->
            <ListItemMenu
              v-if="isCoolingComponent && isActive && hasPermission('legionella.riskAssessment.view')"
              icon="mdi-alert-circle-outline"
              :title="$t('sitePage.assessment')"
              :items="legionellaRiskAssessments"
              disableStyleIconListItemMenu
              @click="getLegionellaRiskAssessments"
              @selected-option="redirect($event, 'assessment')"
            />
            <!-- Risk Assessments -->
            <ListItemMenu
              v-if="hasRiskAssessment && isActive && hasPermission('riskAnalyses.view')"
              icon="mdi-alert"
              :title="$t('sites.riskAssessments')"
              :items="riskAnalyses"
              disableStyleIconListItemMenu
              @click="getRiskAnalyses"
              @selected-option="redirect($event, 'riskAnalysis')"
            />
            <!-- Certificates -->
            <ListItemMenu
              v-if="showItem && hasPermission('certificates.view')"
              icon="mdi-file-certificate"
              :title="$t('sites.tooltip.certification')"
              :items="certificateHistory"
              disableStyleIconListItemMenu
              :isCertificationHistory="true"
              @click="getCertificateHistory(mutableNodeItem)"
              @selected-option="handleCertificatesAction($event,'certificate', 'componentCertificate')"
            />
            <!-- RBQ Reports -->
            <ListItem
              v-if="isCoolingComponent && isActive"
              icon="mdi-file-pdf-box"
              :title="$t('sites.tooltip.coolingNetworkReport')"
              @click="showCoolingNetworkReport(mutableNodeItem)"
            />

            <!-- Add Image -->
            <ListItem
              v-if="nodeType !== SITE && isActive && hasPermission('nodes.edit')"
              icon="mdi-image"
              title="Images"
              @click="onLockedMenuItemClick('nodeImage', {
                node: mutableNodeItem,
                parentNode,
                showBreadcrumbs: true,
                actionName:'nodeImage',
              })"
            />

            <!-- Edit Element -->
            <ListItem
              v-if="hasPermission('nodes.edit') && hasPermission('nodes.types.view')"
              v-show="nodeType !== SITE"
              icon="mdi-pencil"
              :title="$t('sites.tooltip.editNode')"
              @click="onLockedMenuItemClick(`edit${nodeType}`, {
                node : mutableNodeItem,
                parentNode,
                nodeType,
                siteId,
                actionName: `edit${nodeType}`,
              })"
            />

            <!-- Duplicate Network Site -->
            <ListItem
              v-if="isActive && (nodeType=== NETWORK || nodeType === COMPONENT)"
              icon="content_copy"
              :title="$t('sites.tooltip.duplicate')"
              @click="duplicateNode({
                node: mutableNodeItem,
                parentNode,
                showBreadcrumbs: true, })"
            />


            <!-- Restore Node -->
            <ListItem
              v-if="checkButtonVisibility('restore')"
              icon="mdi-archive-refresh"
              class="hidden-md-and-down"
              :title="$t('forms.btn.restore')"
              @click="$emit('nodeRestore',id,site.id)"
            />
            <!-- Copy Site -->
            <ListItem
              v-if="isActive && nodeType === SITE"
              icon="content_copy"
              :title="$t('sites.tooltip.copySite')"
              @click="showCopySite(mutableNodeItem)"
            />
            <!-- Delete Node -->
            <ListItem
              v-if="checkButtonVisibility('delete')"
              icon="mdi-delete"
              class="hidden-md-and-down"
              :title="$t('forms.btn.deactivate')"
              @click="$emit('nodeDelete',site.id, {
                node: mutableNodeItem,
                parentNode,
                showBreadcrumbs: true,})"
            />
            <!-- Edit -->
            <ListItem
              v-if="isActive && nodeType === SITE && hasPermission('nodes.edit') && !isEditable"
              class="-md-and-down"
              icon="mdi-pencil"
              :title="$t('forms.btn.edit')"
              @click="isEditable=true"
            />
            <!-- View -->
            <ListItem
              v-if="isActive && nodeType=== SITE && hasPermission('nodes.edit') && isEditable"
              icon="mdi-eye"
              class="hidden-md-and-down"
              :title="$t('forms.btn.view')"
              @click="isEditable=false"
            />
          </v-list>
        </v-menu>
      </v-toolbar-items>
      <!-- end 3 dot icon -->
    </v-toolbar>
    <v-progress-linear
      v-show="showLoading"
      query
    />
    <div
      v-if="mutableNodeItem.showChildren && !loading && hasPermission('nodes.children.view')"
    >
      <Drag
        v-if="isEditable"
        :nodes="mutableNodes"
        :isEditable="isEditable"
        :parentNode="{
          id,
          name,
          nodeType,
          nodeTypeId,
        }"
        :site="site"
        :depth="depth + 1"
        :path="pathName"
        :showAll="showAll"
        :showChildren="mutableNodeItem.showChildren"
        :parentShowChildren="true"
        @nodeItemChanged="$emit('nodeItemChanged', $event)"
        @dragAndDropEnded="onDrapAndDropEnded"
      />
      <div v-if="!isEditable">
        <div
          v-for="node in mutableNodes"
          :key="node.ref"
        >
          <Tree
            :id="node.ref"
            :name="node.name"
            :nodeType="node.nodeType"
            :nodeTypeId="node.type.id"
            :nodeItem="node"
            :nodes="node.children"
            :parentNode="{
              id,
              name,
              nodeType,
              nodeTypeId,
              typeKey:mutableNodeItem.type ? mutableNodeItem.type.key :null
            }"
            :site="site"
            :depth="depth + 1"
            :path="pathName"
            :showAll="showAll"
            :viewFrom="viewFrom"
            :isSelectMode="isSelectMode"
            :readOnly="readOnly"
            :analysesTableHeads="analysesTableHeads"
            :analysisNormsVals="analysisNormsVals"
            v-on="$listeners"
            @dragAndDropEnded="onDrapAndDropEnded"
          />
        </div>
      </div>
      <v-container v-if="isActive && isNodeEmpty && hasPermission('nodes.analyses.view') && isViewFromSiteTree && !checkIfTechnician()">
        <v-row
          align="center"
          justify="center"
        >
          <h5>{{ emptyMessage }}</h5>
        </v-row>
        <v-row
          v-if="displayButtonAddElement"
          align="center"
          justify="center"
        >
          <v-btn
            color="secondary"
            class="siteTree-btn"
            @click="sendAction(addNode, {
              node: mutableNodeItem,
              parentNode,
              siteId
            })"
          >
            {{ addElement }}
          </v-btn>
        </v-row>
      </v-container>
      <div
        v-else-if="showAnalysis && analyses && hasPermission('nodes.analyses.view')"
        class="compCardContainer"
      >
        <div
          v-if="isViewFromSiteTree"
          class="compCardContent"
        >
          <TreeAnalysisCard
            v-for="analysis in sortedAnalysis"
            :key="analysis.id"
            :analysis="analysis"
            :componentId="id"
            :siteId="siteId"
            :typeOfComponent="getComponentName"
            @reloadAnalysis="reloadComponentConfiguration"
          />
        </div>
        <template v-else>
          <v-simple-table
            fixedheader
            style="max-height: 300px; overflow-y: auto"
          >
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">
                    {{ $t('analyses.analysis') }}
                  </th>
                  <th
                    v-for="head in analysesTableHeads"
                    :key="head.action"
                    class="text-left"
                  >
                    {{ $t(`tree.${head.action}`) }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <template
                  v-for="analysis in analyses"
                >
                  <TreeAnalysisAlertRow
                    :key="analysis.analysisType.id"
                    :analysis="analysis"
                    :componentId="id"
                    :siteId="siteId"
                    :analysesTableHeads="analysesTableHeads"
                    :normsVal="getAnalysisNorms(analysis)"
                    :readOnly="readOnly"
                    @onSelectionChange="onAnalysisNormSelectionChange"
                  />
                </template>
              </tbody>
            </template>
          </v-simple-table>
        </template>
      </div>
    </div>
    <!-- TODO: PLACE THE LOADING WIDGET IN A UPPER LEVEL -->
    <LoadingWidget
      :show="loadingReport"
      :freezeScreen="true"
    />
  </div>
</template>

<script>
import { cloneDeep } from 'lodash';
import moment from 'moment';
import { mapGetters } from 'vuex';
import EventBus from '@/utilities/event-bus';
import treeMixin from '@/mixins/TreeMixin';
import { COMPONENT, NETWORK, CONTAINER, SITE } from '@/aquassist-shared/constants/types/node';
import {
    WATER_CONSUMPTION_ANALYSIS_TYPE, PRODUCT_CONSUMPTION_ANALYSIS_TYPE, SIMPLE_ANALYSIS_TYPE,
    CALCULATED_ANALYSIS_TYPE, FUEL_CONSUMPTION_ANALYSIS_TYPE,
} from '@/aquassist-shared/constants/types/componentAnalysisConfiguration';
import TreeAnalysisCard from '@/components/sites/TreeAnalysisCard/TreeAnalysisCard';
import LoadingWidget from '@/components/shared/widgets/LoadingWidget';
import ListItem from '@/components/shared/widgets/ListItem.vue';
import ListItemMenu from '@/components/shared/widgets/ListItemMenu.vue';
import VCheckbox from '@/components/shared/widgets/forms/VuetifyInputs/VCheckbox.vue';
import roles from '@/constants/roles';
import addAnalysisFormTitle from '@/constants/addAnalysisFormTitle';
import lockService from '@/utilities/lockService';
import TreeAnalysisAlertRow from './TreeAnalysisAlertRow.vue';
import Drag from './Drag.vue';


const { BOILER, COOLING } = require('@/aquassist-shared/constants/types/components');

export default {
    name: 'Tree',
    components: {
        TreeAnalysisCard,
        TreeAnalysisAlertRow,
        ListItem,
        ListItemMenu,
        LoadingWidget,
        Drag,
        VCheckbox,
    },
    mixins: [
        treeMixin,
    ],
    props: {
        active: {
            type: Boolean,
            default: false,
        },
        viewFrom: {
            type: String,
            default: 'SiteTree',
        },
        analysesTableHeads: {
            type: Array,
            default: () => [],
        },
        analysisNormsVals: {
            type: Array,
            default: () => [],
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
        isFocusedProp: {
            type: Boolean,
            default: false,
        },

    },
    data() {
        return {
            loading: false,
            loadingReport: false,
            legionellaRiskAssessments: [],
            flowDiagrams: [],
            schedules: [],
            riskAnalyses: [],
            analyses: null,
            certificates: [],
            mutableNodes: [],
            isEditable: false,
            alertAnalysisTypes: [],
            certificateHistory: [],
            allowedCaracters: 22,
            focusTimeout: null,
            isFocused: this.isFocusedProp,
        };
    },
    computed: {
        WATER_CONSUMPTION_ANALYSIS_TYPE() {
            return WATER_CONSUMPTION_ANALYSIS_TYPE;
        },
        PRODUCT_CONSUMPTION_ANALYSIS_TYPE() {
            return PRODUCT_CONSUMPTION_ANALYSIS_TYPE;
        },
        FUEL_CONSUMPTION_ANALYSIS_TYPE() {
            return FUEL_CONSUMPTION_ANALYSIS_TYPE;
        },
        addAnalysisFormTitle() {
            return addAnalysisFormTitle;
        },
        ...mapGetters([
            'hasPermission',
            'hasRole',
        ]),
        isViewFromSiteTree() {
            return this.viewFrom === 'SiteTree';
        },
        // Cypress test
        getNodeName() {
            return `nodeTreeOptions-${this.nodeName}`;
        },
        addElement() {
            if (this.nodeType === COMPONENT) {
                if (this.hasPermission('analyses.configurations.add')) {
                    return this.$t('sites.nodes.editAnalysis');
                }

                return null;
            }

            return this.$t('sites.nodes.addElement');
        },
        addNode() {
            return this.nodeType === COMPONENT ? 'addAnalysis' : 'addNode';
        },
        showAnalysis() {
            return (
                this.mutableNodeItem.showChildren
              && this.nodeType === COMPONENT
              && this.leaf
              && this.analyses
              && this.analyses.length > 0
            );
        },
        showLoading() {
            return this.loading && (this.nodeType === SITE || this.nodeType === COMPONENT);
        },
        hasRiskAssessment() {
            if (this.nodeType === NETWORK && this.mutableNodeItem && this.mutableNodeItem.type) {
                switch (this.mutableNodeItem.type.key) {
                    case 'steam':
                    case 'closed':
                        return true;
                    default:
                }
            } else if (this.isCoolingComponent) { return true; }

            return false;
        },
        isCoolingNetwork() {
            return this.nodeType === NETWORK && this.mutableNodeItem.type.key === COOLING;
        },
        showItem() {
            if (this.isActive) {
                if ((this.nodeType === NETWORK && !this.isCoolingNetwork)
                || this.isCoolingComponent) return true;
            }

            return false;
        },
        isCoolingComponent() {
            return this.nodeType === COMPONENT && this.parentNode.typeKey === COOLING && this.mutableNodeItem.type.key === COOLING;
        },

        finishedOptions() {
            return {
                name: this.$t('forms.labels.finishedVersions'),
                isDisabled: true,
            };
        },
        sortedAnalysis() {
            // Analysis are grouped by analysisType type and sorted in each group by analysisType displayOrder
            // As the analyses can't be a huge array we keep sorting on frontend
            const sortedAnalyses = [];
            const simpleAnalyses = this.analyses
                .filter(a => a.analysisType.type === SIMPLE_ANALYSIS_TYPE || a.analysisType.type === CALCULATED_ANALYSIS_TYPE)
                .sort((a, b) => a.analysisType.displayOrder - b.analysisType.displayOrder);

            const waterAnalyses = this.analyses
                .filter(a => a.analysisType.type === WATER_CONSUMPTION_ANALYSIS_TYPE)
                .sort((a, b) => a.analysisType.displayOrder - b.analysisType.displayOrder);

            const productAnalyses = this.analyses
                .filter(a => a.analysisType.type === PRODUCT_CONSUMPTION_ANALYSIS_TYPE)
                .sort((a, b) => a.analysisType.displayOrder - b.analysisType.displayOrder);

            const fuelAnalyses = this.analyses
                .filter(a => a.analysisType.type === FUEL_CONSUMPTION_ANALYSIS_TYPE)
                .sort((a, b) => a.analysisType.displayOrder - b.analysisType.displayOrder);

            if (simpleAnalyses.length > 0) {
                sortedAnalyses.push(...simpleAnalyses);
            }
            if (waterAnalyses.length > 0) {
                sortedAnalyses.push(...waterAnalyses);
            }
            if (productAnalyses.length > 0) {
                sortedAnalyses.push(...productAnalyses);
            }
            if (fuelAnalyses.length > 0) {
                sortedAnalyses.push(...fuelAnalyses);
            }
            return sortedAnalyses;
        },
        iconName() {
            let name = '';
            switch (this.mutableNodeItem.nodeType) {
                case COMPONENT:
                    name = 'gas_meter';
                    break;
                case NETWORK:
                    name = 'hub';
                    break;
                case CONTAINER:
                    name = 'developer_board';
                    break;
                default:
                    name = 'business';
                    break;
            }
            return name;
        },
        COMPONENT() {
            return COMPONENT;
        },
        NETWORK() {
            return NETWORK;
        },
        CONTAINER() {
            return CONTAINER;
        },
        SITE() {
            return SITE;
        },
        getComponentName() {
            if (this.isCoolingComponent) return COOLING;
            return BOILER;
        },
    },
    watch: {
        showAll(value) {
            const site = this.toggleAllChildren({ ...this.nodeItem }, value);
            this.nodeItemChanged(site);
        },
        nodes: {
            handler(val) {
                this.mapNodes(val);
            },
            deep: true,
            immediate: true,
        },
        analysisNormsVals: {
            handler(newValue) {
                if (newValue.length > 0) {
                    this.alertAnalysisTypes = cloneDeep(newValue);
                }
            },
            immediate: true,
            deep: true,
        },
        isFocusedProp(newVal) {
            this.isFocused = newVal;
        },
    },
    mounted() {
        EventBus.$on('call-method-on-node', ({ nodeId, parentNodeId, methodName, methodArgs = [] }) => {
            if (methodName && nodeId && this[methodName]) {
                this[methodName].apply(this, [nodeId, parentNodeId, methodArgs]);
            }
        });
    },
    beforeUpdate() {
        if (this.nodes.isEmpty) {
            this.loading = false;
        }
    },
    beforeDestroy() {
        clearTimeout(this.focusTimeout);
    },
    methods: {
        async onLockedMenuItemClick(action, payload) {
            if (payload.node && payload.node.nodeType === COMPONENT) {
                const componentId = payload.node.ref;
                const lockAcquired = await lockService.requestLock(componentId, payload.actionName);
                if (!lockAcquired) {
                    EventBus.$emit('open-lock-queue-modal', {
                        componentId,
                        retrieveLock: async () => lockService.requestLock(componentId, payload.actionName),
                        retryFunction: () => {
                            this.sendAction(action, payload);
                        },
                    });
                    return;
                }
            }
            this.sendAction(action, payload);
        },
        sendAction(action, payload) {
            EventBus.$emit('node-action', [action, payload]);
        },

        showNodeId(componentId, parentNodeId) {
            if (this.id === componentId || this.id === parentNodeId) {
                this.nodeItemChanged({ ...this.mutableNodeItem, nodeType: this.nodeType, showChildren: true });

                this.toggleChildren(componentId);
                this.handleNodeFocus(componentId);
            }
        },
        toggleChildren(componentId) {
            if (this.nodeType === COMPONENT && this.isSelectMode) return;

            if ((this.nodeType === COMPONENT && this.hasPermission('nodes.analyses.view') && !this.mutableNodeItem.showChildren)
            || (this.nodeType === COMPONENT && componentId)) {
                this.getComponentConfigurations(componentId || this.id);
            }


            if (!componentId) {
                if (this.site?.children) {
                    const component = this.findNode(this.site.children, this.mutableNodeItem.ref);
                    if (component) {
                        // keep track if the node in site for future use
                        component.showChildren = !component.showChildren;
                    }
                }
                this.nodeItemChanged({ ...this.mutableNodeItem, nodeType: this.nodeType, showChildren: !this.mutableNodeItem.showChildren });
            }
        },
        toggleFocus(bool) {
            this.isFocused = bool;
        },
        handleNodeFocus(componentId) {
            clearTimeout(this.focusTimeout);
            if (this.id === componentId) {
                this.toggleFocus(true);
                this.focusTimeout = setTimeout(() => {
                    if (this.id === componentId) {
                        this.toggleFocus(false);
                    }
                }, 10000);
            }
        },
        async getComponentConfigurations(componentId) {
            this.loading = true;
            const { data } = await this.$rq.getComponentConfigurations(componentId);
            this.analyses = data.configurations.filter(configuration => configuration.active);
            this.$set(this.mutableNodeItem, 'analyses', this.analyses);
            this.loading = false;
        },
        prepareHistory(items) {
            return items.map(({ id, updatedBy, updatedAt }) => ({
                id,
                name: `${this.$options.filters.formatDate(updatedAt)} - ${updatedBy.firstName} ${updatedBy.lastName}`,
            }));
        },
        introOptions(isNewDisabled = false) {
            return [{
                name: this.$t('forms.labels.new'),
                isDisabled: isNewDisabled,
                isNew: true,
            }, {
                name: this.$t('forms.labels.draft'),
                isDisabled: true,
            }];
        },
        prepareOptions(items, status = 'finished') {
            const unfinished = items.filter(item => !item[status]);
            const options = [...this.introOptions(unfinished.length > 0)];

            options.push(...this.prepareHistory(unfinished));

            const finished = items.filter(item => item[status]);

            if (finished.length) {
                options.push(
                    this.finishedOptions,
                    ...this.prepareHistory(finished),
                );
            }

            return options;
        },
        async getLegionellaRiskAssessments() {
            const componentId = this.mutableNodeItem.ref;
            const networkId = this.parentNode.id;
            const { data } = await this.$rq.getLegionellaRiskAssessments({ networkId, componentId });
            this.legionellaRiskAssessments = this.prepareOptions(data);
        },
        async getRiskAnalyses() {
            const query = { network: this.mutableNodeItem.ref };
            if (this.nodeType === COMPONENT) {
                query.network = this.parentNode.id;
                query.component = this.mutableNodeItem.ref;
            }
            const { data } = await this.$rq.getRiskAnalyses(query);
            this.riskAnalyses = this.prepareOptions(data.riskAnalyses);
        },
        async onClickRedirect(name) {
            await this.$router.push({
                name,
                params: {
                    networkId: this.mutableNodeItem.ref,
                },
            });
        },
        async redirectToComponentOrNetworkRoute(networkRoute, componentRoute) {
            let pathParams = { networkId: this.mutableNodeItem.ref };
            let name = networkRoute;
            if (componentRoute && this.nodeType === COMPONENT) {
                name = componentRoute;
                pathParams = {
                    componentId: this.mutableNodeItem.ref,
                    networkId: this.parentNode.id,
                };
                const componentId = this.mutableNodeItem.ref;
                const lockAcquired = await lockService.requestLock(componentId, networkRoute);
                if (!lockAcquired) {
                    EventBus.$emit('open-lock-queue-modal', {
                        componentId,
                        retrieveLock: async () => lockService.requestLock(componentId, networkRoute),
                        retryFunction: () => {
                            this.$store.dispatch('setLockFrom', true);
                            this.$router.push({
                                name,
                                params: {
                                    ...pathParams,
                                },
                            });
                        },
                    });
                    return;
                }
            }
            this.$store.dispatch('setLockFrom', true);
            await this.$router.push({
                name,
                params: {
                    ...pathParams,
                },
            });
        },
        async redirect(option = {}, route) {
            let routeName = route;
            let newRoute = `new${this.$options.filters.capitalize(route)}`;
            if (this.nodeType === COMPONENT) {
                newRoute = `${newRoute}${COMPONENT}`;
                routeName = `${route}${COMPONENT}`;
            }
            const params = this.nodeType === COMPONENT
                ? { componentId: this.mutableNodeItem.ref, networkId: this.parentNode.id }
                : { networkId: this.mutableNodeItem.ref };
            if (option.id) {
                params[`${route}Id`] = option.id;
            }
            if (this.nodeType === COMPONENT) {
                const componentId = this.mutableNodeItem.ref;
                const lockAcquired = await lockService.requestLock(componentId, routeName);
                if (!lockAcquired) {
                    EventBus.$emit('open-lock-queue-modal', {
                        componentId,
                        retrieveLock: async () => lockService.requestLock(componentId, routeName),
                        retryFunction: () => {
                            this.$store.dispatch('setLockFrom', true);
                            this.$router.push({
                                name: option.id ? routeName : newRoute,
                                params,
                            });
                        },
                    });
                    return;
                }
            }
            this.$store.dispatch('setLockFrom', true);
            this.$router.push({
                name: option.id ? routeName : newRoute,
                params,
            });
        },
        async showCoolingNetworkReport(node) {
            this.loadingReport = true;

            this.$toasted.show(this.$t('tree.toasterMessage.waiting'), {
                duration: null,
                className: 'toast-custom-waiting',
            });
            try {
                // Report type fetch just for the component(s) of the cooling network
                const { data } = await this.$rq.getCoolingNetworkReport(this.parentNode.id, { componentId: node.ref });

                if (data.size > 0) {
                    this.$toasted.success(this.$t(this.$t('tree.toasterMessage.success'), {
                        duration: 5000,
                        singleton: true,
                    }));
                }

                const now = moment().format('YYYY-MM-DD');
                const fileName = `${node.name} - ${this.$t('sites.tooltip.coolingNetworkReport')} - ${now}.pdf`;
                await this.$utils.saveFileOnDisk(data, fileName);
            } catch (error) {
                this.$toasted.global.toast_error({
                    message: this.$t('tree.toasterMessage.error'),
                    duration: 5000,
                    singleton: true,

                });
            } finally {
                this.loadingReport = false;
            }
        },


        checkButtonVisibility(btnType) {
            let visbilityStats = false;
            if ((this.nodeType.toLowerCase() !== 'site') && this.hasPermission('nodes.edit')) {
                if (btnType === 'delete') {
                    visbilityStats = this.mutableNodeItem.active;
                } else if (btnType === 'restore') {
                    visbilityStats = !this.mutableNodeItem.active;
                }
            }
            return visbilityStats;
        },
        getAnalysisNorms({ analysisType: { id: analysisId }, component: { id: componentId } }) {
            const [analysis] = this.alertAnalysisTypes.filter(an => an.id === analysisId && an.component === componentId);
            return (analysis || {});
        },
        onAnalysisNormSelectionChange(updatedAnalysisType) {
            // check if the analysis type exist we update it with the new norm value
            // Otherwise we add a new alert analysis type
            const existingIndex = this.alertAnalysisTypes.findIndex(an => an.id === updatedAnalysisType.id
                                          && an.component === updatedAnalysisType.component);
            if (existingIndex !== -1) {
                const existingAnalysisType = this.alertAnalysisTypes[existingIndex];
                const key = updatedAnalysisType.normLevel;
                const value = updatedAnalysisType.normVal;
                this.alertAnalysisTypes.splice(existingIndex, 1, {
                    ...existingAnalysisType,
                    norms: {
                        ...existingAnalysisType.norms,
                        [key]: value,
                    },
                });
            } else {
                const { normLevel, normVal, ...res } = updatedAnalysisType;
                const newAlertAnalysisType = {
                    ...res,
                    norms: { [updatedAnalysisType.normLevel]: updatedAnalysisType.normVal },
                };
                this.alertAnalysisTypes.push(newAlertAnalysisType);
            }
            this.$emit('analysisNormsChange', this.alertAnalysisTypes);
        },
        checkBoxValueChanged() {
            let node = this.mutableNodeItem;
            if (this.mutableNodeItem?.children) {
                node = {
                    ...node,
                    children: this.mapChildren(this.mutableNodeItem.children),
                };
            }
            this.nodeItemChanged(node);
            this.$emit('checkBoxValueChanged', { ...node, parentNode: this.parentNode ? this.parentNode.id : null });
        },
        mapChildren(children) {
            return children.map(node => ({
                ...node,
                children: node.children ? this.mapChildren(node.children) : undefined,
                isSelected: this.mutableNodeItem.isSelected,
            }));
        },
        async getCertificateHistory(nodeItem) {
            let networkId = nodeItem.ref;
            const params = {};
            if (nodeItem.nodeType === COMPONENT) {
                networkId = nodeItem.parentNode;
                params.componentId = nodeItem.ref;
            }
            const { data } = await this.$rq.getCertificatesHistory(networkId, params);
            this.certificateHistory = data.map(certificate => {
                const name = certificate.certificate.fileName.length <= this.allowedCaracters ? certificate.certificate.fileName
                    : `${certificate.certificate.fileName.slice(0, this.allowedCaracters)}...`;
                return {
                    ...certificate, name: `${this.$options.filters.formatDate(certificate.createdAt, 'l')} - ${name}`,
                };
            });
            this.certificateHistory.push({ name: `${this.$t('sitePage.add')} / ${this.$t('forms.btn.edit')}` });
        },
        async handleCertificatesAction(event, path, component) {
            if (!event.id) {
                this.redirectToComponentOrNetworkRoute(path, component);
            } else if (this.hasPermission('files.view')) {
                const { data } = await this.$rq.downloadFile(event.certificate.id);
                this.$utils.saveFileOnDisk(data, event.certificate.fileName);
            }
        },
        async reloadComponentConfiguration() {
            await this.getComponentConfigurations(this.id);
        },
        displayButtonAddElement() {
            if (this.hasRole(roles.SERVICE_TECHNICIAN.value)
            || this.hasRole(roles.LABORATORY_TECHNICIAN.value)
            || this.hasRole(roles.ROLE_OPERATOR.value)) {
                return false;
            }
            return true;
        },
        checkIfTechnician() {
            if (this.hasRole(roles.LABORATORY_TECHNICIAN.value) || (this.hasRole(roles.SERVICE_TECHNICIAN.value))) return true;
            return false;
        },
        mapNodes(array) {
            this.mutableNodes = array.map(node => ({
                ...node,
                showChildren: node.showChildren || false,
                isSelected: node.isSelected || false,
            }));
        },
        toggleAllChildren(node, value) {
            if (node.children) {
                // eslint-disable-next-line no-param-reassign
                node = {
                    ...node,
                    children: [...node.children.map(n => {
                        if (n.children) return this.toggleAllChildren(n, value);
                        return { ...n, showChildren: n.nodeType !== COMPONENT ? value : false };
                    })],
                };
            }
            return { ...node, showChildren: node.nodeType !== COMPONENT ? value : false };
        },
        onDrapAndDropEnded(data) {
            this.$emit('dragAndDropEnded', data);
        },


    },
};
</script>

<style lang="scss" scoped>
@import '@/assets/styles/vuetify_variables.scss';
  .tree-toolbar {
    &.theme--light {
      .v-toolbar__content {
        .v-toolbar__items {
          .v-icon {
            color: #fff;
          }
        }
      }
    }
  }
  .v-list-item::v-deep {
    .v-icon {
      margin-right: 8px;
    }
  }
  .checkBox {
    margin-bottom: 0px !important;
  }

  .focus-node .siteTree-title::after {
  content: "";
  display: inline-block;
  width: 24px;
  height: 24px;
  background-image: url('/dashboard/greenExclamation.svg');
  background-size: cover;
  background-repeat: no-repeat;
  margin-left: 4px;
}
  .bloc-tree-title {
    overflow: hidden;
    width: 100%;
    margin-left: 10px;
  }

  .toast-custom-waiting {
    z-index: 10000000000 !important;
  }

  .compCardContainer {
      padding: 1rem 1rem 1rem 0;
      .compCardContent {
          display: grid;
          gap: 10px;
          @media #{map-get($display-breakpoints, 'lg-and-up')} {
              grid-template-columns: repeat(3, 1fr);
          }
          @media #{map-get($display-breakpoints, 'md-only')} {
              grid-template-columns: repeat(2, 1fr);
          }
          @media #{map-get($display-breakpoints, 'sm-and-down')} {
              grid-template-columns: 1fr;
          }
      }
  }

</style>
